// Note: sendResponse and sendError are backend utility functions, not needed in frontend

export interface SiteSettings {
  general: {
    company_name: string;
    primary_color: string;
    secondary_color: string;
    logo_url: string;
    favicon_url: string;
    currency: string;
  };
  contact: {
    main_email: string;
    main_phone: string;
    whatsapp_number: string;
    support_email: string;
    offices: {
      [key: string]: {
        country: string;
        city: string;
        address: string;
        phone: string;
        email: string;
        flag: string;
        timezone: string;
      };
    };
  };
  statistics: {
    companies_formed: number;
    countries_served: number;
    client_satisfaction: number;
    years_experience: number;
    support_availability: string;
  };
  social: {
    facebook_url: string;
    linkedin_url: string;
    twitter_url: string;
    instagram_url: string;
  };
  footer: {
    // Visibility Controls
    footer_enabled: boolean;
    company_section_enabled: boolean;
    services_section_enabled: boolean;
    quicklinks_section_enabled: boolean;
    contact_section_enabled: boolean;
    social_links_enabled: boolean;
    bottom_bar_enabled: boolean;
    wave_separator_enabled: boolean;
    animations_enabled: boolean;
    // Content - Company Section
    company_description_en: string;
    company_description_ar: string;
    company_logo_enabled: boolean;
    // Content - Section Headings
    services_heading_en?: string;
    services_heading_ar?: string;
    quicklinks_heading_en?: string;
    quicklinks_heading_ar?: string;
    contact_heading_en?: string;
    contact_heading_ar?: string;
    // Content - Contact Information
    footer_email: string;
    footer_support_hours_en?: string;
    footer_support_hours_ar?: string;
    whatsapp_contact_enabled: boolean;
    // Content - Copyright & Legal
    copyright_text_en: string;
    copyright_text_ar: string;
    show_legal_links?: boolean;
    // Design Settings - Colors
    background_color?: string;
    primary_color?: string;
    heading_color?: string;
    text_color?: string;
    link_color?: string;
    link_hover_color?: string;
    icon_color?: string;
    icon_background_color?: string;
    wave_color?: string;
    divider_color?: string;
    background_gradient_start?: string;
    background_gradient_end?: string;
    text_primary_color?: string;
    text_secondary_color?: string;
    // Services Configuration
    display_all_countries?: boolean;
    selected_countries?: string[];
    // Social Media Toggles
    show_twitter?: boolean;
    show_linkedin?: boolean;
    show_facebook?: boolean;
    show_whatsapp_social?: boolean;
  };
}

export interface SiteContent {
  [category: string]: {
    [key: string]: string;
  };
}

class SettingsService {
  private baseUrl = 'https://patrixglobal.com/backend/api';
  private cache: { [key: string]: any } = {};
  private cacheExpiry: { [key: string]: number } = {};
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes

  private isCacheValid(key: string): boolean {
    return this.cache[key] && this.cacheExpiry[key] && Date.now() < this.cacheExpiry[key];
  }

  private setCache(key: string, data: any): void {
    this.cache[key] = data;
    this.cacheExpiry[key] = Date.now() + this.cacheTimeout;
  }

  private clearCache(key?: string): void {
    if (key) {
      delete this.cache[key];
      delete this.cacheExpiry[key];
    } else {
      this.cache = {};
      this.cacheExpiry = {};
    }
  }

  // Public API methods (no authentication required)
  async getPublicSettings(category?: string): Promise<Partial<SiteSettings>> {
    const cacheKey = `public_settings_${category || 'all'}`;

    if (this.isCacheValid(cacheKey)) {
      return this.cache[cacheKey];
    }

    try {
      const url = category
        ? `${this.baseUrl}/settings?category=${category}`
        : `${this.baseUrl}/settings`;

      const response = await fetch(url);

      // Check if response is empty or not JSON
      const text = await response.text();
      if (!text) {
        throw new Error('Empty response from server');
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {        throw new Error('Invalid JSON response from server');
      }

      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}: Failed to fetch settings`);
      }

      if (data.success) {
        this.setCache(cacheKey, data.data);
        return data.data;
      } else {
        throw new Error(data.message || 'Failed to fetch settings');
      }
    } catch (error) {      throw error;
    }
  }

  async getPublicContent(category?: string, language: string = 'en'): Promise<SiteContent> {
    const cacheKey = `public_content_${category || 'all'}_${language}`;

    if (this.isCacheValid(cacheKey)) {
      return this.cache[cacheKey];
    }

    try {
      const url = category
        ? `${this.baseUrl}/content?category=${category}&language=${language}`
        : `${this.baseUrl}/content?language=${language}`;

      const response = await fetch(url);

      // Check if response is empty or not JSON
      const text = await response.text();
      if (!text) {
        throw new Error('Empty response from server');
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {        throw new Error('Invalid JSON response from server');
      }

      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}: Failed to fetch content`);
      }

      if (data.success) {
        this.setCache(cacheKey, data.data);
        return data.data;
      } else {
        throw new Error(data.message || 'Failed to fetch content');
      }
    } catch (error) {      throw error;
    }
  }

  // Admin API methods (authentication required)
  async getAdminSettings(category?: string): Promise<any> {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      throw new Error('Admin authentication required');
    }

    try {
      const url = category
        ? `${this.baseUrl}/admin/settings?category=${category}`
        : `${this.baseUrl}/admin/settings`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch admin settings');
      }

      if (data.success) {
        return data.data;
      } else {
        throw new Error(data.message || 'Failed to fetch admin settings');
      }
    } catch (error) {      throw error;
    }
  }

  async updateAdminSettings(category: string, settings: any): Promise<void> {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      throw new Error('Admin authentication required');
    }

    try {
      const response = await fetch(`${this.baseUrl}/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          category,
          settings
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update settings');
      }

      if (!data.success) {
        throw new Error(data.message || 'Failed to update settings');
      }

      // Clear relevant caches
      this.clearCache(`public_settings_${category}`);
      this.clearCache('public_settings_all');
    } catch (error) {      throw error;
    }
  }

  async updateSingleSetting(category: string, key: string, value: any): Promise<void> {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      throw new Error('Admin authentication required');
    }

    try {
      const response = await fetch(`${this.baseUrl}/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          category,
          setting_key: key,
          setting_value: value
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update setting');
      }

      if (!data.success) {
        throw new Error(data.message || 'Failed to update setting');
      }

      // Clear relevant caches
      this.clearCache(`public_settings_${category}`);
      this.clearCache('public_settings_all');
    } catch (error) {      throw error;
    }
  }

  async getAdminContent(category?: string, language: string = 'en'): Promise<any> {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      throw new Error('Admin authentication required');
    }

    try {
      const url = category
        ? `${this.baseUrl}/admin/settings/content?category=${category}&language=${language}`
        : `${this.baseUrl}/admin/settings/content?language=${language}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch admin content');
      }

      if (data.success) {
        return data.data;
      } else {
        throw new Error(data.message || 'Failed to fetch admin content');
      }
    } catch (error) {      throw error;
    }
  }

  async updateAdminContent(category: string, content: any, language: string = 'en'): Promise<void> {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      throw new Error('Admin authentication required');
    }

    try {
      const response = await fetch(`${this.baseUrl}/admin/settings/content`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          category,
          language,
          content
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update content');
      }

      if (!data.success) {
        throw new Error(data.message || 'Failed to update content');
      }

      // Clear relevant caches
      this.clearCache(`public_content_${category}_${language}`);
      this.clearCache(`public_content_all_${language}`);
    } catch (error) {      throw error;
    }
  }

  // Utility methods
  clearAllCaches(): void {
    this.clearCache();
  }

  // Quick access methods for commonly used settings
  async getCompanyName(): Promise<string> {
    const settings = await this.getPublicSettings('general');
    return settings.general?.company_name || 'Patrix Global';
  }

  async getContactInfo(): Promise<Partial<SiteSettings['contact']>> {
    const settings = await this.getPublicSettings('contact');
    return settings.contact || {};
  }

  async getStatistics(): Promise<Partial<SiteSettings['statistics']>> {
    const settings = await this.getPublicSettings('statistics');
    return settings.statistics || {};
  }

  async getWhatsAppNumber(): Promise<string> {
    const settings = await this.getPublicSettings('contact');
    return settings.contact?.whatsapp_number || '+1234567890';
  }

  async getOffices(): Promise<any> {
    const settings = await this.getPublicSettings('contact');
    return settings.contact?.offices || {};
  }
}

const settingsService = new SettingsService();
export default settingsService;